"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Zap, Calculator, Music, Waves, BookOpen, Telescope } from 'lucide-react';
import { AboutSidebar } from '@/components/about/sidebar';

export default function HarmonicsPage() {
  return (
    <div className="min-h-screen bg-slate-950">
      <div className="flex h-screen">
        {/* Sidebar */}
        <AboutSidebar activeSection="astrology" />

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-4xl mx-auto px-4 py-8">
            {/* Current Section Indicator */}
            <div className="mb-4 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-emerald-300">
                <Telescope className="h-4 w-4" />
                <span className="text-sm font-medium">🔭 Astroloji</span>
                <span className="text-emerald-500">/</span>
                <span className="text-sm">Gelişmiş Sistemler</span>
                <span className="text-emerald-500">/</span>
                <span className="text-sm">Harmonik Astroloji</span>
              </div>
            </div>

            {/* Breadcrumb */}
            <div className="mb-6">
              <Link
                href="/about"
                className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Astroloji
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link
                href="/about/astrology/advanced-systems"
                className="text-slate-400 hover:text-slate-200 transition-colors"
              >
                Gelişmiş Sistemler
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Harmonik Astroloji</span>
            </div>

            {/* Header */}
            <div className="bg-gradient-to-r from-emerald-900/20 to-teal-900/20 rounded-xl p-8 mb-8 border border-emerald-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Zap className="h-10 w-10 text-emerald-400" />
                Harmonik Astroloji
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                John Addey tarafından geliştirilen, natal haritanın matematiksel harmoniklerini inceleyen ileri düzey sistem.
                Her harmonik sayı farklı bir yaşam alanını temsil eder ve gizli potansiyelleri ortaya çıkarır.
              </p>
            </div>

            {/* Basic Theory */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Temel Teori</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Music className="h-5 w-5 text-emerald-400" />
                      Harmonik Prensibi
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Müzikteki harmonikal frekanslar gibi, astrolojide de her sayının
                        kendine özgü bir "frekansı" ve anlamı vardır.
                      </p>
                      <div className="bg-emerald-900/20 border border-emerald-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-emerald-200 mb-2">Temel Prensip:</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>• Her harmonik sayı bir yaşam temasyını yansıtır</div>
                          <div>• Harmonikler saklı potansiyelleri açığa çıkarır</div>
                          <div>• Çoklu bakış açısı sağlar</div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 flex items-center gap-2">
                      <Calculator className="h-5 w-5 text-teal-400" />
                      Hesaplama Yöntemi
                    </h3>
                    <div className="space-y-3">
                      <p className="text-slate-300">
                        Harmonik hesaplama: Gezegen pozisyonu × Harmonik sayı
                      </p>
                      <div className="bg-teal-900/20 border border-teal-700/50 rounded-lg p-4">
                        <h4 className="font-semibold text-teal-200 mb-2">Örnek Hesaplama (H4):</h4>
                        <div className="space-y-1 text-sm text-slate-300">
                          <div>Güneş: 15° Koç (15°)</div>
                          <div>H4: 15° × 4 = 60° = 0° İkizler</div>
                          <div>360°'yi geçerse, 360 çıkarılır</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* John Addey */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">John Addey ve Tarihsel Gelişim</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-amber-300">John Addey (1920-1982)</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      İngiliz astrolog ve matematikçi. Harmonik astrolojiyi sistematik
                      bir bilim dalı haline getiren öncü çalışmalar yaptı.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>"Harmonics in Astrology" (1976):</strong> Ana eseri</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Astrological Association:</strong> Başkanlığı</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-amber-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>İstatistiksel araştırmalar:</strong> Bilimsel yaklaşım</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-xl font-semibold text-slate-100 mb-4 text-blue-300">Modern Gelişim</h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Harmonik astroloji, bilgisayar teknolojisi ile birlikte
                      daha geniş kullanım alanı buldu ve gelişti.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>David Hamblin:</strong> Addey'nin öğrencisi ve devam ettiricisi</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Michael Harding:</strong> Modern harmonik yaklaşım</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400"><strong>Bilgisayar yazılımları:</strong> Kolay hesaplama</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Major Harmonics */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Ana Harmonikler</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* H2 */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-red-700 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">2</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">İkinci Harmonik (H2)</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Polarite ve Denge</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">İlişkiler ve ortaklıklar</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">Denge arayışı</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-red-400 rounded-full"></div>
                        <span className="text-slate-400">Karşıtlıkların birleşimi</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* H3 */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-700 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">3</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Üçüncü Harmonik (H3)</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">İletişim ve Öğrenme</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                        <span className="text-slate-400">Zihinsel aktivite</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                        <span className="text-slate-400">Öğrenme ve öğretme</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
                        <span className="text-slate-400">Sosyal etkileşim</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* H4 */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-700 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">4</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Dördüncü Harmonik (H4)</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Yapı ve Düzen</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-slate-400">Materyal başarı</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-slate-400">Pratik yetenekler</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                        <span className="text-slate-400">Organizasyon becerisi</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* H5 */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">5</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Beşinci Harmonik (H5)</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Yaratıcılık ve İfade</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Sanatsal yetenek</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Yaratıcı projeler</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                        <span className="text-slate-400">Kişisel ifade</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* H7 */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-purple-700 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">7</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Yedinci Harmonik (H7)</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Inspirasyon ve Sezgi</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                        <span className="text-slate-400">Ruhsal gelişim</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                        <span className="text-slate-400">Mistik deneyimler</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                        <span className="text-slate-400">İçgörü ve aydınlanma</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* H9 */}
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-indigo-700 rounded-full flex items-center justify-center">
                      <span className="text-white font-bold">9</span>
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Dokuzuncu Harmonik (H9)</h3>
                  </div>
                  <div className="space-y-2">
                    <p className="text-slate-300 text-sm font-medium">Bilgelik ve Genişleme</p>
                    <div className="space-y-1 text-xs">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                        <span className="text-slate-400">Felsefi düşünce</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                        <span className="text-slate-400">Yüksek öğrenim</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 bg-indigo-400 rounded-full"></div>
                        <span className="text-slate-400">Evrensel bakış açısı</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advanced Harmonics */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">İleri Harmonikler</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-slate-100 text-cyan-300">H12 - Ruhsal Bütünlük</h3>
                    <div className="space-y-2 text-sm">
                      <div className="text-slate-300">• Karma ve geçmiş yaşamlar</div>
                      <div className="text-slate-300">• Ruhsal tamamlanma</div>
                      <div className="text-slate-300">• Fedakarlık ve hizmet</div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-slate-100 text-pink-300">H16 - Dönüşüm</h3>
                    <div className="space-y-2 text-sm">
                      <div className="text-slate-300">• Derin psikolojik değişim</div>
                      <div className="text-slate-300">• Gizli güçlerin ortaya çıkışı</div>
                      <div className="text-slate-300">• Yeniden doğuş süreçleri</div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <h3 className="text-lg font-semibold text-slate-100 text-yellow-300">H24 - Usta Sayı</h3>
                    <div className="space-y-2 text-sm">
                      <div className="text-slate-300">• Ustaca beceri gelişimi</div>
                      <div className="text-slate-300">• Mükemmeliyetçilik</div>
                      <div className="text-slate-300">• Teknik uzmanlık</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Practical Application */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Pratik Uygulama</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Waves className="h-5 w-5 text-teal-400" />
                    Harmonik Harita Okuma
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Her harmonik harita o sayının temasını vurgular.
                      Güçlü aspectler ve stelliumlar önemli ipuçları verir.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-teal-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Conjunction'ları (0°) öncelikle inceleyin</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-teal-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">T-Square ve Grand Trine gibi major konfigürasyonlara odaklanın</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-teal-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Natal harita ile karşılaştırın</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                    <Calculator className="h-5 w-5 text-emerald-400" />
                    Yorumlama İpuçları
                  </h3>
                  <div className="space-y-3">
                    <p className="text-slate-300">
                      Harmonikler natal potansiyelin farklı katmanlarını
                      gösterir ve gizli yetenekleri açığa çıkarır.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Her harmonik bir "frekans" yaratır</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Güçlü harmonikler yaşamda öne çıkar</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <div className="w-2 h-2 bg-emerald-400 rounded-full mt-1.5 flex-shrink-0"></div>
                        <span className="text-sm text-slate-400">Transit harmonikleri de kontrol edin</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Study Resources */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-slate-100 mb-4 flex items-center gap-2">
                <BookOpen className="h-5 w-5" />
                Önerilen Kaynaklar
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">Temel Eserler</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Harmonics in Astrology" - John Addey</div>
                    <div>• "The Astrology of Midpoints" - Michael Munkasey</div>
                    <div>• "Working with Astrology" - Michael Harding</div>
                  </div>
                </div>
                <div>
                  <h4 className="font-semibold text-slate-200 mb-3">İleri Düzey Çalışmalar</h4>
                  <div className="space-y-2 text-sm text-slate-400">
                    <div>• "Harmonic Charts" - David Hamblin</div>
                    <div>• "The Astrology of the Seers" - David Frawley</div>
                    <div>• "Research into Lost Knowledge" - John Addey</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Important Note */}
            <div className="bg-red-900/20 border border-red-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-red-200 mb-3">⚠️ Önemli Açıklama</h3>
              <p className="text-red-100 leading-relaxed">
                Harmonik astroloji ileri düzey matematiksel hesaplamalar gerektiren kompleks bir tekniktir.
                Bu bilgiler sadece eğitim amaçlıdır ve hiçbiri bilimsel kesinlik iddiası taşımaz.
                Profesyonel analiz için deneyimli astrologlarla çalışılmalıdır.
              </p>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/astrology/advanced-systems/midpoints"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Orta Noktalar
              </Link>

              <Link
                href="/about/astrology/advanced-systems/draconic"
                className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors"
              >
                Draconic Harita
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}